<template>
    <Head :title="t('assessments.rater.declined.pageTitle')" />
    <RaterChrome :max-width="600" :show-anonymous="false">
        <div v-if="loading" class="body-small text-ink-muted pt-10">{{ t("assessments.rater.declined.loading") }}</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss class="mt-10" />
        <template v-else-if="invite">
            <div class="declined">
                <div class="declined__badge" aria-hidden="true">
                    <i class="bi bi-check-lg" aria-hidden="true"></i>
                </div>
                <h1 class="display-serif text-ink leading-tight">
                    <span v-if="declined">{{ t("assessments.rater.declined.title") }}</span>
                    <span v-else>{{ t("assessments.rater.declined.inactive.title") }}</span>
                </h1>
                <p v-if="declined" class="body-small text-ink-muted max-w-md">
                    {{ subjectFirstName ? t("assessments.rater.declined.bodyNamed", { name: subjectFirstName }) : t("assessments.rater.declined.bodyUnnamed") }}
                </p>
                <p v-else class="body-small text-ink-muted max-w-md">{{ t("assessments.rater.declined.inactive.body") }}</p>
                <div class="declined__actions">
                    <a :href="closeHref" class="base-btn-neutral base-btn-sm inline-flex items-center gap-2"> {{ t("common.close") }} </a>
                </div>
            </div>
        </template>
    </RaterChrome>
</template>

<script setup lang="ts">
import { Head } from "@inertiajs/vue3";
import BaseNotification from "~vue/components/BaseNotification.vue";
import RaterChrome from "~vue/components/RaterChrome.vue";
import { useInviteLoader } from "~vue/composables/useInviteLoader";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { markPagePublic } from "~vue/utils/sessionEnded";
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    invite_token: string;
    is_anonymous: boolean;
}

const props = defineProps<Props>();
const { t } = useI18n();

// An external rater has no account to log back into, so a rejected token there must stay silent.
// An internal rater is signed in, and a real session expiry must still offer them recovery.
if (props.is_anonymous) {
    markPagePublic();
}

const { invite, loading, loadError } = useInviteLoader(props.invite_token);

watch(invite, (loaded) => {
    if (!loaded) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_DECLINED_VIEWED, {
        declined: loaded.state === "revoked",
    });
});

const declined = computed(() => invite.value?.state === "revoked");
const subjectFirstName = computed(() => invite.value?.subject_first_name ?? "");
const closeHref = computed(() => (invite.value?.is_authenticated ? "/" : "https://valence.co"));
</script>

<style scoped>
@reference "../../../css/tailwind.css";

.declined {
    @apply flex flex-col items-center text-center gap-4;
    padding: 40px 0 8px;
}

.declined__badge {
    @apply flex items-center justify-center text-ink-muted bg-surface-sunken;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    margin-bottom: 8px;
    font-size: 26px;
    animation: declinedFadeIn 0.35s ease-out both;
}

.declined__actions {
    @apply flex gap-2.5;
    margin-top: 20px;
}

@keyframes declinedFadeIn {
    from {
        opacity: 0;
        transform: translateY(4px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (prefers-reduced-motion: reduce) {
    .declined__badge {
        animation: none;
    }
}
</style>
